/**
 * Amazon Connect Agent Availability States
 */
export enum AgentAvailStates {
  Init = 'Init',
  Busy = 'Busy',
  AfterCallWork = 'AfterCallWork',
  CallingCustomer = 'CallingCustomer',
  Dialing = 'Dialing',
  Joining = 'Joining',
  PendingAvailable = 'PendingAvailable',
  PendingBusy = 'PendingBusy',
}

/**
 * Custom Connect agent status shown when the agent misses an inbound call/chat
 * (we did not pick up from our side). The generic NOT_ROUTABLE state is not
 * enough — Connect reports several not-routable statuses, and this one must be
 * selected by name.
 */
export const FORCED_RELEASE_STATE = 'Forced Release';

/**
 * Amazon Connect Agent Error States
 */
export enum AgentErrorStates {
  Error = 'Error',
  AgentHungUp = 'AgentHungUp',
  BadAddressAgent = 'BadAddressAgent',
  BadAddressCustomer = 'BadAddressCustomer',
  Default = 'Default',
  FailedConnectAgent = 'FailedConnectAgent',
  FailedConnectCustomer = 'FailedConnectCustomer',
  InvalidLocale = 'InvalidLocale',
  LineEngagedAgent = 'LineEngagedAgent',
  LineEngagedCustomer = 'LineEngagedCustomer',
  MissedCallAgent = 'MissedCallAgent',
  MissedCallCustomer = 'MissedCallCustomer',
  MultipleCcpWindows = 'MultipleCcpWindows',
  RealtimeCommunicationError = 'RealtimeCommunicationError',
}

/**
 * Helper function to check if a state name is an error state
 */
export function checkIsErrorState(stateName: string): boolean {
  const stateLower = stateName.toLowerCase();
  return Object.values(AgentErrorStates).some(
    errorState => stateLower === errorState.toLowerCase()
  );
}

/**
 * Helper function to check if a state name is After Call Work
 */
export function checkIsAfterCallWorkState(stateName: string): boolean {
  const stateLower = stateName.toLowerCase();
  return stateLower === AgentAvailStates.AfterCallWork.toLowerCase();
}

/**
 * Helper function to check if a state is available/online
 * Note: "Available" and "Online" are typically custom state names, so we check against enum values
 */
export function checkIsAvailableState(stateName: string): boolean {
  const stateLower = stateName.toLowerCase();
  return (
    stateLower === AgentAvailStates.PendingAvailable.toLowerCase() ||
    stateLower === 'available' ||
    stateLower === 'online'
  );
}

/**
 * Helper function to check if a state is offline
 * Note: "Offline" is typically a custom state name
 */
export function checkIsOfflineState(stateName: string): boolean {
  const stateLower = stateName.toLowerCase();
  return stateLower === 'offline';
}

/**
 * Helper function to check if a state is routable
 * Note: "Routable" is typically a custom state name, but we check for exact matches
 */
export function checkIsRoutableState(stateName: string): boolean {
  const stateLower = stateName.toLowerCase();
  return (
    stateLower === 'routable' ||
    stateLower === 'available' ||
    stateLower === 'online'
  );
}

/**
 * Helper function to check if a state is not routable
 * Note: "Not Routable" is typically a custom state name
 */
export function checkIsNotRoutableState(stateName: string): boolean {
  const stateLower = stateName.toLowerCase();
  return (
    stateLower === 'not_routable' ||
    stateLower === 'not routable' ||
    stateLower === AgentAvailStates.PendingBusy.toLowerCase() ||
    stateLower === AgentAvailStates.Busy.toLowerCase()
  );
}
